import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Search } from "lucide-react";
import { COUNTRIES, PRODUCTS, COMPLIANCE_PAIRS } from "@/lib/content_compliance";
import SEO from "@/components/SEO";

export default function ImportIndex() {
  const [country, setCountry] = useState("all");
  const [product, setProduct] = useState("all");
  const [q, setQ] = useState("");

  const visible = COMPLIANCE_PAIRS.filter((it) => {
    if (country !== "all" && it.country.code !== country) return false;
    if (product !== "all" && it.product.slug !== product) return false;
    if (q.trim()) {
      const s = q.toLowerCase();
      return it.country.name.toLowerCase().includes(s) || it.product.name.toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div data-testid="import-index" className="bg-white">
      <SEO
        title={`Import From China · ${COMPLIANCE_PAIRS.length} Compliance Briefs`}
        description={`${COMPLIANCE_PAIRS.length} pre-built customs duty, VAT/sales tax and certification briefs for importing from China across ${COUNTRIES.length} destination markets, maintained by Fortune Sourcings.`}
        path="/import"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Import From China — Compliance Library",
          "description": `Pre-built compliance briefs covering customs duty, VAT/sales tax and mandatory certifications for the most-imported product categories from China, across ${COUNTRIES.length} destination markets.`,
          "url": "https://fortunesourcings.com/import",
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": COMPLIANCE_PAIRS.length,
            "itemListElement": COMPLIANCE_PAIRS.slice(0, 60).map((it, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "url": `https://fortunesourcings.com/import/${it.product.slug}/from-china/to/${it.country.code}`,
              "name": `Import ${it.product.name} from China to ${it.country.name}`,
            })),
          },
        }}
      />
      <section className="bg-[#12283F] text-white py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-4"><span className="gold-rule" /><span className="eyebrow text-[#B8965A]">Import from China · Compliance Library</span></div>
          <h1 className="font-serif text-5xl lg:text-7xl leading-[0.98] max-w-4xl">
            {COMPLIANCE_PAIRS.length} pre-built compliance briefs.
          </h1>
          <p className="text-white/65 text-lg max-w-3xl mt-6 leading-relaxed">
            Latest 2026 customs duty, VAT/sales tax, mandatory certifications and Fortune Sourcings on-ground notes for the most-imported product categories from China, across {COUNTRIES.length} of our top destination markets.
          </p>
        </div>
      </section>

      <section className="bg-white py-10 border-b border-[#E2E8F0]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <input
              data-testid="import-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search country or product…"
              className="w-full border border-[#E2E8F0] pl-10 pr-4 py-2.5 text-sm focus:border-[#B8965A] outline-none"
            />
          </div>
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="border border-[#E2E8F0] px-3 py-2.5 text-sm bg-white focus:border-[#B8965A] outline-none" data-testid="import-country">
            <option value="all">All destinations</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
          <select value={product} onChange={(e) => setProduct(e.target.value)} className="border border-[#E2E8F0] px-3 py-2.5 text-sm bg-white focus:border-[#B8965A] outline-none" data-testid="import-product">
            <option value="all">All categories</option>
            {PRODUCTS.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
          </select>
        </div>
      </section>

      <section className="bg-white py-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-xs text-[#6B7280] mb-5">Showing <strong className="text-[#1C3E5F]">{visible.length}</strong> of {COMPLIANCE_PAIRS.length} briefs</div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {visible.map((it) => (
              <Link
                key={`${it.country.code}-${it.product.slug}`}
                to={`/import/${it.product.slug}/from-china/to/${it.country.code}`}
                data-testid={`import-pair-${it.product.slug}-${it.country.code}`}
                className="block bg-white border border-[#E2E8F0] hover:border-[#B8965A] p-5 group transition-colors"
              >
                <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.16em] text-[#B8965A]">
                  <MapPin size={10} />
                  China → {it.country.name}
                </div>
                <div className="font-serif text-[#1C3E5F] text-xl leading-tight mb-2 group-hover:text-[#B8965A]">{it.product.name}</div>
                <div className="text-[11px] text-[#1C3E5F]/60 leading-relaxed mb-3">HS {it.product.hs}</div>
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[#1C3E5F] font-semibold">
                  View brief <ArrowRight size={10} />
                </span>
              </Link>
            ))}
          </div>

          {visible.length === 0 && (
            <div className="py-14 text-center text-[#6B7280]">No matches, try clearing filters.</div>
          )}
        </div>
      </section>
    </div>
  );
}
